import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, mergeMap, switchMap, throwError } from 'rxjs';
import { ApiService } from '../../services/api.service';
import {
  RETRIEVE_CONFIG,
  RETRIEVE_CONFIG_SUCCESS,
  RETRIEVE_EXPIRED_OBJECT,
  RETRIEVE_EXPIRED_OBJECT_SUCCESS,
  RETRIEVE_NOTIFICATIONS,
  RETRIEVE_NOTIFICATIONS_SUCCESS,
  RETRIEVE_REGEX_DHL,
  RETRIEVE_REGEX_DHL_SUCCESS,
  RETRIEVE_SHOW_SURVEY,
  RETRIEVE_SURVEY,
  RETRIEVE_SURVEY_SUCCESS,
} from './config.actions';
import { ResponseNotifications } from '../../interfaces/response-notifications';
import { ResponseSurvey } from '../../interfaces/response-survey';
import { ResponseRegexDhl } from '../../interfaces/response-regex-dhl';
import { ResponseExpiredObjects } from '../../interfaces/response-expired-objects';
import { Config } from '../../interfaces/config';
import { publicPaths } from '../../utils/menu-config';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ADD_MESSAGE } from '../messages/messages.actions';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class ConfigEffects {
  retrieveConfig$ = createEffect(() => {
    let currentToken = '';
    return this.actions$.pipe(
      ofType(RETRIEVE_CONFIG),
      switchMap(({ token }) => {
        currentToken = token;
        return this.api.retrieveConfig();
      }),
      mergeMap((response: Config) => {
        if (publicPaths.some((path) => this.router.url.indexOf(path) >= 0)) {
          this.router.navigateByUrl('/login');
        }

        return [
          RETRIEVE_CONFIG_SUCCESS({
            config: response,
          }),
          ...(currentToken && !response.isMaintainance
            ? [
                RETRIEVE_NOTIFICATIONS(),
                RETRIEVE_SURVEY(),
                RETRIEVE_REGEX_DHL(),
                RETRIEVE_EXPIRED_OBJECT(),
              ]
            : []),
        ];
      }),
      catchError((error: HttpErrorResponse) => {
        this.addErrorMessage(error.message)
        return throwError({ error });
      })
    );
  });

  retrieveNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RETRIEVE_NOTIFICATIONS),
      switchMap(() => this.api.retrieveNotifications()),
      mergeMap((response: ResponseNotifications) => {
        return [
          RETRIEVE_NOTIFICATIONS_SUCCESS({
            isNotificationPresent: response.nuovaComunicazione === '1',
            messaggioHeader: response.messaggioHeader,
            nuovaComunicazione: response.nuovaComunicazione,
          }),
        ];
      }),
      catchError((error: HttpErrorResponse) => {
        this.addErrorMessage(error.message)
        return throwError({ error });
      })
    )
  );

  retrieveSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RETRIEVE_SURVEY),
      switchMap(() => this.api.retrieveSurvey()),
      mergeMap((response: ResponseSurvey) => {
        return [
          RETRIEVE_SURVEY_SUCCESS({
            surveyUrl: response.url,
          }),
          RETRIEVE_SHOW_SURVEY({
            abilitato: response.abilitato,
          }),
        ];
      }),
      catchError((error: HttpErrorResponse) => {
        this.addErrorMessage(error.message)
        return throwError({ error });
      })
    )
  );

  retrieveRegexDhl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RETRIEVE_REGEX_DHL),
      switchMap(() => this.api.retrieveRegexDhl()),
      mergeMap(
        ({
          regexCodiceDhlInbound,
          regexCodiceDhlInboundCodeSeparator,
        }: ResponseRegexDhl) => {
          return [
            RETRIEVE_REGEX_DHL_SUCCESS({
              regexCodiceDhlInbound,
              regexCodiceDhlInboundCodeSeparator,
            }),
          ];
        }
      ),
      catchError((error: HttpErrorResponse) => {
        this.addErrorMessage(error.message)
        return throwError({ error });
      })
    )
  );

  retrieveExpiredObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RETRIEVE_EXPIRED_OBJECT),
      switchMap(() => this.api.retrieveExpiredObjects()),
      mergeMap(
        ({
          frequenzaPopupOggettiScaduti,
          giornataLavorativa,
          numeroOgettiScadutiOggi,
          numeroOgettiScadutiPrimaDiOggi,
          oggettiScadutiOggi,
          oggettiScadutiPrimaDiOggi,
        }: ResponseExpiredObjects) => {
          return [
            RETRIEVE_EXPIRED_OBJECT_SUCCESS({
              frequenzaPopupOggettiScaduti,
              giornataLavorativa,
              numeroOgettiScadutiOggi,
              numeroOgettiScadutiPrimaDiOggi,
              oggettiScadutiOggi,
              oggettiScadutiPrimaDiOggi,
            }),
          ];
        }
      ),
      catchError((error: HttpErrorResponse) => {
        this.addErrorMessage(error.message)
        return throwError({ error });
      })
    )
  );

  constructor(
    private actions$: Actions,
    private api: ApiService,
    private router: Router,
    private store: Store
  ) {}

  private addErrorMessage(message: string): void {
    this.store.dispatch(ADD_MESSAGE({ message: message }));
  }
}
